import { Alert, Autocomplete, FormControlLabel, IconButton, Switch, TextField } from '@mui/material'
import Checkbox from '@mui/material/Checkbox'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import Dropdown, { IOption } from 'components/Dropdown/Dropdown'
import { formatInTimeZone, getTimezoneOffset, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'
import { useIsMobile } from 'hooks/useIsMobile'
import { ReactComponent as DeleteSvg } from 'images/icons/delete.svg'
import React, { useEffect, useMemo, useState } from 'react'
import { dateTimePickerStyles } from 'utils/dateTimePickerStyles'
import { formatTimezoneLabel, guessBrowserTimezone, timezoneAutocompleteOptions } from 'utils/timezones'

import style from '../TaskSettingsModal.module.css'

const weekdaysLabels = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

interface SchedulerSettingsProps {
  initialDate: Date | null
  initialEndDate: Date | null
  initialWeekdays: boolean[]
  timezone: string
  showWeekdaysSelection?: boolean
  robotTimezone?: string | null
  onSchedulerChange: (date: Date | null, endDate: Date | null, weekdays: boolean[]) => void
  onTimezoneChange: (timezone: string) => void
}

const SchedulerSettings = ({
  initialDate,
  initialEndDate,
  initialWeekdays,
  timezone,
  showWeekdaysSelection,
  robotTimezone,
  onSchedulerChange,
  onTimezoneChange,
}: SchedulerSettingsProps) => {
  const [date, setDate] = useState<Date | null>(initialDate)
  const [endDate, setEndDate] = useState<Date | null>(initialEndDate)
  const [weekdays, setWeekdays] = useState<boolean[]>(initialWeekdays)
  const isMobile = useIsMobile()
  const [initialRender, setInitialRender] = useState(true)

  useEffect(() => {
    setDate(initialDate)
  }, [initialDate])

  useEffect(() => {
    if (initialRender) {
      setWeekdays(initialWeekdays)
      setInitialRender(false)
    }
  }, [initialWeekdays, initialRender])

  const getSchedulerDateFromPickerDate = (pickerDate: Date | null) => {
    if (!pickerDate) return pickerDate
    try {
      return zonedTimeToUtc(pickerDate, timezone)
    } catch {
      return pickerDate
    }
  }

  const getPickerDateFromSchedulerDate = (schedulerDate: Date | null) => {
    if (!schedulerDate) return schedulerDate
    try {
      return utcToZonedTime(schedulerDate, timezone)
    } catch {
      return schedulerDate
    }
  }

  const handleDateChange = (newPickerDate: Date | null) => {
    const newDate = getSchedulerDateFromPickerDate(newPickerDate)
    setDate(newDate)
    onSchedulerChange(newDate, endDate, weekdays)
    // console.log("Selected date", date);
  }
  const handlEndDateChange = (newDate: Date | null) => {
    setEndDate(newDate)
    onSchedulerChange(date, newDate, weekdays)
    // console.log("Selected endDate", endDate);
  }

  const handleWeekdayChange = (dayIndex: number) => {
    const updatedWeekdays = [...weekdays]
    updatedWeekdays[dayIndex] = !updatedWeekdays[dayIndex]
    setWeekdays(updatedWeekdays)
    onSchedulerChange(date, endDate, updatedWeekdays)
  }

  const handleClearDate = () => {
    setDate(null)
    onSchedulerChange(null, endDate, weekdays)
    onTimezoneChange(robotTimezone ?? browserTimezone)
  }

  const handleClearEndDate = () => {
    setEndDate(null)
    onSchedulerChange(date, null, weekdays)
  }

  const browserTimezone = guessBrowserTimezone()

  const timezoneOptions = useMemo(
    () => timezoneAutocompleteOptions(timezone, robotTimezone, browserTimezone),
    [timezone, robotTimezone, browserTimezone]
  )

  useEffect(() => {
    if (robotTimezone && timezone !== robotTimezone) {
      onTimezoneChange(robotTimezone)
    }
  }, [robotTimezone]) // eslint-disable-line react-hooks/exhaustive-deps

  const getOffsetMinutes = (tz: string | null | undefined) => {
    if (!tz) return null
    try {
      const offsetMs = getTimezoneOffset(tz, date ?? new Date())
      return Number.isFinite(offsetMs) ? Math.round(offsetMs / 60000) : null
    } catch {
      return null
    }
  }

  const robotOffset = getOffsetMinutes(robotTimezone)
  const browserOffset = getOffsetMinutes(browserTimezone)

  const isTimezoneMismatch = robotOffset !== null && browserOffset !== null && robotOffset !== browserOffset
  const pickerDate = getPickerDateFromSchedulerDate(date)
  const pickerReferenceDate = getPickerDateFromSchedulerDate(new Date()) ?? undefined
  const formatLocalTime = (utcDate: Date | null) => {
    if (!utcDate) return ''
    try {
      return formatInTimeZone(utcDate, browserTimezone, 'dd.MM.yyyy HH:mm')
    } catch {
      return ''
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div className={style.dateTimeContainer}>
        <DateTimePicker
          label="Choose date and time"
          value={pickerDate}
          onChange={handleDateChange}
          referenceDate={pickerReferenceDate}
          ampm={false}
          slots={{ textField: TextField }}
          slotProps={dateTimePickerStyles({ isMobile })}
        />
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <div
            style={{
              position: 'absolute',
              top: '-22px',
              right: '12px',
              fontSize: '12px',
              color: 'var(--text-secondary)',
              whiteSpace: 'nowrap',
            }}
          >
            {robotTimezone ? `Robot's timezone` : 'Select timezone'}
          </div>
          <Autocomplete
            className={style.scheduleTimezone}
            disabled={!!robotTimezone}
            sx={{
              width: isMobile ? 120 : 240,
              '& .MuiInputBase-root': {
                height: 36,
                backgroundColor: 'var(--glass-bg)',
                color: 'var(--text-main)',
                borderRadius: 'var(--radius-lg)',
                fontFamily: 'var(--font-main)',
                '& fieldset': {
                  border: 'var(--form-border) !important',
                  borderRadius: 'var(--radius-lg)',
                },
                '&:hover fieldset': {
                  borderColor: 'var(--color-info) !important',
                },
                '& .MuiSvgIcon-root': {
                  color: 'var(--text-secondary) !important',
                },
              },
              '& .MuiAutocomplete-input': {
                color: 'var(--text-main) !important',
                fontSize: '14px',
                fontFamily: 'var(--font-main)',
              },
            }}
            slotProps={{
              paper: {
                sx: {
                  background: 'var(--glass-bg) !important',
                  backdropFilter: 'var(--glass-blur) !important',
                  WebkitBackdropFilter: 'var(--glass-blur) !important',
                  borderRadius: 'var(--radius-md) !important',
                  boxShadow: 'var(--glass-shadow) !important',
                  border: 'var(--form-border) !important',
                  color: 'var(--text-main) !important',
                  width: 'max-content !important',
                  minWidth: '280px !important',
                  '& .MuiAutocomplete-option': {
                    fontFamily: 'var(--font-main)',
                    fontSize: '14px',
                    '&:hover': {
                      backgroundColor: 'var(--glass-bg-hover) !important',
                    },
                    '&[aria-selected="true"]': {
                      backgroundColor: 'var(--color-info) !important',
                      color: 'white !important',
                    },
                  },
                },
              },
            }}
            options={timezoneOptions}
            getOptionLabel={option => option.label}
            value={
              timezoneOptions.find(o => o.value === timezone) || {
                value: timezone,
                label: formatTimezoneLabel(timezone),
              }
            }
            onChange={(event, newValue) => {
              if (newValue) {
                onTimezoneChange(newValue.value)
              }
            }}
            renderInput={params => <TextField {...params} variant="outlined" size="small" />}
          />
        </div>
        <IconButton
          className={style.clearDateButton}
          onClick={handleClearDate}
          aria-label="Clear date and time"
          size="small"
        >
          <DeleteSvg className={style.removeIcon} />
        </IconButton>
      </div>
      {isTimezoneMismatch && date && (
        <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px', paddingLeft: '4px' }}>
          User's local time: {formatLocalTime(date)} ({browserTimezone.replace(/_/g, ' ')})
        </div>
      )}
      {isTimezoneMismatch && robotTimezone && (
        <div style={{ marginTop: '16px' }}>
          <Alert severity="info">
            Your browser timezone ({browserTimezone}) differs from the robot's timezone ({robotTimezone}). The task will
            use the robot's timezone.
          </Alert>
        </div>
      )}
      {showWeekdaysSelection && (
        <div>
          <FormControlLabel
            control={
              <Checkbox
                checked={weekdays.every(Boolean)}
                onChange={() => {
                  const allChecked = weekdays.every(Boolean)
                  const newWeekdays = weekdays.map(() => !allChecked)
                  setWeekdays(newWeekdays)
                  onSchedulerChange(date, endDate, newWeekdays) // TO DO
                }}
              />
            }
            label="Set a cleaning schedule by day of the week"
            sx={{ paddingTop: 3, marginLeft: isMobile ? '6px' : '0px' }}
          />
          {weekdays.some(Boolean) && (
            <div className={style.weekdays}>
              {weekdays.map((dayChecked, index) => (
                <FormControlLabel
                  key={index}
                  control={<Switch checked={dayChecked} onChange={() => handleWeekdayChange(index)} />}
                  label={weekdaysLabels[index]}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </LocalizationProvider>
  )
}
export default SchedulerSettings
